/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';
import { set, action, computed } from '@ember/object';
import { isNone, isEmpty } from '@ember/utils';
import { task } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import { inject as controller } from '@ember/controller';
import isEqual from 'lodash.isequal';
import FormValidations from 'cio/utils/form-validations';
import isValidEmail from 'cio/utils/validators/email';
import UserModel from 'cio/models/user';
import AnalyticsService from 'cio/services/analytics';
import { SelfServeMigrateTo, IdentifierState, IdentifierKey } from 'cio/utils/identifiers';
import FeaturesService from 'ember-feature-flags/services/features';
import AccountService from 'cio/services/account';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import AuthService from 'cio/services/auth';
import { updateChannelStateForMeasure } from 'cio/utils/metrics/measure';
import AnalysisController from 'cio/pods/analysis/controller';
import EnvironmentModel, { DeliverySettings } from 'cio/models/environment';
import { EditableEnvironment, EditableEnvironmentProperty } from 'cio/utils/environment/editable';

interface SettingsEnvironmentFormArgs {
  environment: EnvironmentModel;
  action: 'create' | 'edit';
  users: UserModel[];
  hydraUsersPermissions?: { user_ids: (number | string)[] };
  readOnly: boolean;
  showSettingsAlert: boolean;
  onSubmit: () => Promise<void>;
  onCancel?: () => void;
}

const Validations = {
  name(this: SettingsEnvironmentForm, val: string) {
    if (isNone(val) || isEmpty(val.trim())) {
      return "Workspace name can't be blank";
    }

    let nameExists: boolean;
    let lowerCaseVal = val.toLowerCase().trim();
    if (this.args.action === 'create') {
      nameExists = this.environments.some(
        (environment: EnvironmentModel) => environment.name.toLowerCase() === lowerCaseVal,
      );
    } else {
      nameExists = this.environments.some(
        (environment: EnvironmentModel) =>
          this.args.environment.name?.toLowerCase() !== lowerCaseVal && environment.name.toLowerCase() === lowerCaseVal,
      );
    }
    if (nameExists) {
      return `Workspace name ${val} already exists`;
    }

    return true;
  },

  deliverySettings: {
    dependentKeys: ['deliveryEmail'],
    validator(_: string): string | true {
      return true;
    },
  },

  deliveryEmail: {
    dependentKeys: ['deliverySettings'],
    validator(value: string, model: EditableEnvironment) {
      if (model.deliverySettings === DeliverySettings.SendToOne && !isValidEmail(value)) {
        return 'Please enter a valid email address';
      }

      return true;
    },
  },
};

const EDITABLE_PROPERTIES: EditableEnvironmentProperty[] = [
  'name',
  'hexCode',
  'deliverySettings',
  'deliveryEmail',
  'users',
  'identifiers',
  'emailOpenTracking',
  'identifierSettings',
  'defaultTemplateEngine',
];

const DELIVERY_SETTINGS_OPTIONS = [
  {
    value: 'normal',
    label: 'Send messages normally',
    detail: 'All messages send as defined in your workflows.',
  },
  {
    value: 'send_to_one',
    label: 'Test email delivery',
    detail: 'Emails send to your test address. Everything else sends as normal.',
  },
  {
    value: 'off',
    label: 'Never send messages',
    detail: 'All message delivery is disabled.',
  },
];

export default class SettingsEnvironmentForm extends Component<SettingsEnvironmentFormArgs> {
  @service store!: DS.Store;
  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service features!: FeaturesService;
  @service account!: AccountService;

  validations: FormValidations;

  @tracked private mutableEnvironment!: EditableEnvironment;
  @tracked hasEditedName = false;
  @tracked isMultiIdMigrationModalOpen = false;
  @tracked isEmailTrackingModalOpen = false;
  @tracked isLiquidUpgradeModalOpen = false;
  @tracked activeModal: SelfServeMigrateTo | null = null;

  @controller('analysis') analysisController!: AnalysisController;

  get shouldDisplayJourneysSettings() {
    return this.account.isJourneysAvailable;
  }

  @computed('mutableEnvironment.users.[]', 'args.environment.users.[]')
  get hasUpdatedUsers() {
    return (
      this.args.environment.users.length !== this.mutableEnvironment.users.length ||
      !(this.args.environment.users.content as UserModel[]).every((user) =>
        this.mutableEnvironment.users.includes(user),
      )
    );
  }

  @computed('mutableEnvironment.identifiers.id')
  get isChangingIdMutability() {
    return this.args.environment.identifiers?.id !== this.mutableEnvironment.identifiers?.id;
  }

  @computed('mutableEnvironment.deliverySettings')
  get isChangingDeliverySettings() {
    return this.args.environment.deliverySettings !== this.mutableEnvironment.deliverySettings;
  }

  @computed('mutableEnvironment.deliveryEmail')
  get isChangingDeliveryEmail() {
    return this.args.environment.deliveryEmail !== this.mutableEnvironment.deliveryEmail;
  }

  @computed(
    `args.environment.{${EDITABLE_PROPERTIES.join(',')}}`,
    `mutableEnvironment.{${EDITABLE_PROPERTIES.join(',')},identifierSettings.identify_by_id}`,
    'hasUpdatedUsers',
  )
  get isFormDirty() {
    let hasDirtyAttributes = EDITABLE_PROPERTIES.any((prop) => {
      let defaultValue =
        prop === 'users' ? (this.args.environment[prop].content as UserModel[]).toArray() : this.args.environment[prop];
      return !isEqual(this.mutableEnvironment[prop], defaultValue);
    });
    return hasDirtyAttributes || this.hasUpdatedUsers;
  }

  @computed(
    'validations.{isTouched,hasErrors}',
    'isFormDirty',
    'submitTask.isRunning',
    'args.readOnly',
    'mutableEnvironment.name',
  )
  get submitDisabled() {
    return (
      !this.validations.isTouched ||
      this.validations.hasErrors ||
      !this.isFormDirty ||
      this.submitTask.isRunning ||
      this.args.readOnly ||
      !this.mutableEnvironment.name
    );
  }

  @computed('mutableEnvironment.hexCode')
  get environmentColorSelected() {
    return !!this.mutableEnvironment.hexCode;
  }

  @computed('args.environment.{isNew,identifierType,enabledIdentifiers.[]}')
  get migrateTo() {
    if (this.args.environment.get('isNew')) {
      return null;
    }

    let currentEnv = this.args.environment;

    if (currentEnv?.isMultiIdentifier && currentEnv?.canIdentifyByEmail) {
      return SelfServeMigrateTo.IdOnly;
    }

    return SelfServeMigrateTo.MultiId;
  }

  @computed('args.environment.{isEmailIdentified,isMultiIdentifier,canIdentifyByEmail}')
  get selfServeMigrationHint() {
    let currentEnvironment = this.args.environment;

    if (!currentEnvironment) {
      return;
    }

    if (currentEnvironment.isEmailIdentified) {
      return 'Do you want to make id changeable?';
    } else if (currentEnvironment.isMultiIdentifier && currentEnvironment.canIdentifyByEmail) {
      return 'Do you want to disable email?';
    } else {
      return 'Do you want both id and email to be enabled and changeable?';
    }
  }

  get environments() {
    return this.store.peekAll('environment').filter((environment) => !environment.isNew);
  }

  constructor(owner: unknown, args: SettingsEnvironmentFormArgs) {
    super(owner, args);
    this.setupEnvironment();

    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'mutableEnvironment',
    });
  }

  submitTask = task(async (evt?: Event) => {
    evt?.preventDefault();

    await this.validations.validateModel();

    if (this.isChangingDeliverySettings) {
      this.analytics.track('workspace', 'delivery_settings_updated', {
        previousDeliverySettings: this.args.environment.deliverySettings,
        deliverySettings: this.mutableEnvironment.deliverySettings,
      });
    }
    if (this.isChangingDeliveryEmail) {
      this.analytics.track('workspace', 'delivery_email_updated');
    }
    // Check if user is migrating between workspace types, send request if so.
    if (this.isChangingIdMutability) {
      let migrateTo =
        this.mutableEnvironment.identifiers?.id === IdentifierState.EnabledImmutable
          ? SelfServeMigrateTo.IdOnly
          : SelfServeMigrateTo.IdMutable;
      let data: Record<string, string> = { migrate_to: migrateTo };
      await this.api.post('migrate', { data });
      await this.args.environment.reload();
    }

    let { environment } = this.args;
    let emailOpenTrackingChanged = this.mutableEnvironment.emailOpenTracking !== environment.emailOpenTracking;
    let identifyByIdChanged =
      this.mutableEnvironment.identifierSettings.identify_by_id !== environment.identifierSettings.identify_by_id;
    let defaultTemplateEngineChanged =
      this.mutableEnvironment.defaultTemplateEngine !== environment.defaultTemplateEngine;

    // If email tracking has been turned off and is being saved, interrupt with
    // a modal explaining the effects of turning email tracking off. If they
    // decide to continue with the change, then fully commit the change.
    if (
      !environment.isNew &&
      emailOpenTrackingChanged &&
      !this.mutableEnvironment.emailOpenTracking &&
      !this.isEmailTrackingModalOpen
    ) {
      this.toggleEmailOpenTrackingModal();
      return;
    } else if (this.isEmailTrackingModalOpen) {
      this.toggleEmailOpenTrackingModal();
    }

    environment.setProperties(this.mutableEnvironment);

    if (!this.environmentColorSelected) {
      environment.set('hexCode', '');
    } else if (!environment.hexCode) {
      environment.set('hexCode', environment.hexCodeWithDefault);
    }

    try {
      await this.args.onSubmit();

      if (emailOpenTrackingChanged) {
        this.analytics.track(
          'workspace',
          this.mutableEnvironment.emailOpenTracking ? 'open_tracking_enabled' : 'open_tracking_disabled',
        );
        updateChannelStateForMeasure('opened', 'email', environment.emailOpenTracking);
        updateChannelStateForMeasure('clicked_to_opened', 'email', environment.emailOpenTracking);
        // Reset query params since the opened metric is a default and needs
        // to be re-computed now that open tracking has changed
        this.analysisController.resetMeasureQueryParams();
      }

      if (identifyByIdChanged) {
        this.analytics.track('workspace', 'update_identifiers_by_id_changed', {
          identifyById: environment.identifierSettings.identify_by_id,
        });
      }

      if (defaultTemplateEngineChanged) {
        // eslint-disable-next-line spellcheck/spell-checker
        this.analytics.track('renderer', 'version_change_completed', {
          version: environment.defaultTemplateEngine,
          isWorkspaceSetting: true,
        });
      }
    } catch (e) {
      this.hasEditedName = false;
      this.validations.set('isTouched', false);
      // Rethrow, so that the CioSaveBar component can show the api error toast
      throw e;
    }
  });

  @action
  setColor(val: string) {
    this.validations.set('isTouched', true);
    set(this.mutableEnvironment, 'hexCode', val);
  }

  @computed('mutableEnvironment.deliverySettings')
  get deliverySettingsOptions() {
    return DELIVERY_SETTINGS_OPTIONS.map((option) => {
      let selected = option.value === this.mutableEnvironment.deliverySettings;
      let disabled =
        this.args.readOnly ||
        (this.features.isEnabled('test_delivery_mode') &&
          option.value === DeliverySettings.Normal &&
          (!this.account.current.verificationApproved || !this.hasFromAddressSetUp));

      return {
        ...option,
        selected,
        disabled,
      };
    });
  }

  get isEmailInputDisabled() {
    return (
      this.features.isEnabled('test_delivery_mode') &&
      (!this.account.current.verificationApproved || !this.hasFromAddressSetUp)
    );
  }

  get hasFromAddressSetUp() {
    return this.args.environment.identities.length > 0;
  }

  @computed('mutableEnvironment.defaultTemplateEngine')
  get isLegacyLiquid() {
    return this.mutableEnvironment.defaultTemplateEngine === 0;
  }

  get canSwitchTemplateEngine() {
    return this.args.environment.canUpdateDefaultTemplateEngine || this.features.isEnabled('template_engine_switch');
  }

  @action
  setDeliverySettings(val: DeliverySettings) {
    set(this.mutableEnvironment, 'deliverySettings', val);
    this.validations.validateAttribute('deliverySettings');
  }

  @action
  onChangeUsers(users: UserModel[]) {
    this.validations.set('isTouched', true);
    set(this.mutableEnvironment, 'users', users);
  }

  @action
  onChangeName(name: string) {
    this.validations.set('isTouched', true);
    this.hasEditedName = true;
    set(this.mutableEnvironment, 'name', name);
  }

  @action
  toggleLiquidUpgradeModalOpen() {
    this.isLiquidUpgradeModalOpen = !this.isLiquidUpgradeModalOpen;

    // eslint-disable-next-line spellcheck/spell-checker
    this.analytics.track('renderer', 'version_change_started', {
      currentVersion: this.args.environment.defaultTemplateEngine,
      isWorkspaceSetting: true,
    });
  }

  @action
  switchDefaultTemplateEngine() {
    this.validations.set('isTouched', true);

    let newDefaultTemplateEngineValue = this.args.environment.defaultTemplateEngine === 0 ? 1 : 0;
    set(this.mutableEnvironment, 'defaultTemplateEngine', newDefaultTemplateEngineValue);

    this.isLiquidUpgradeModalOpen = false;
  }

  @action
  setupEnvironment() {
    let {
      name,
      hexCode,
      deliveryEmail,
      deliverySettings,
      users,
      identifierType,
      identifiers,
      isNew,
      emailOpenTracking,
      defaultTemplateEngine,
      identifierSettings,
    } = this.args.environment;

    let selectedUsers = (users.content as UserModel[]).toArray();

    if (!this.account.isJourneysAvailable) {
      // If the account is CDP-only, we need to manually filter the users
      // who have permissions to the workspace, as these permissions
      // don't come from the environment model, but rather from Hydra
      // workspaces.
      let usersArray = this.args.users.toArray();
      selectedUsers = usersArray.filter((user) => {
        return this.args.hydraUsersPermissions?.user_ids?.map(String).includes(String(user.id)) ?? false;
      });
    }

    this.mutableEnvironment = {
      name,
      hexCode,
      deliveryEmail,
      deliverySettings,
      users: selectedUsers,
      identifierType,
      identifiers,
      emailOpenTracking,
      defaultTemplateEngine,
      // Clone the object since JS objects are passed by reference
      identifierSettings: { ...identifierSettings },
    };

    if (isNew as unknown as boolean) {
      return;
    }

    this.args.environment.reload();
  }

  @action
  onUpdate() {
    let {
      name,
      hexCode,
      deliveryEmail,
      deliverySettings,
      users,
      identifierType,
      identifiers,
      emailOpenTracking,
      defaultTemplateEngine,
      identifierSettings,
    } = this.args.environment;

    if (!this.validations.isTouched) {
      this.mutableEnvironment = {
        name,
        hexCode,
        deliveryEmail,
        deliverySettings,
        users: (users.content as UserModel[]).toArray(),
        identifierType,
        identifiers,
        emailOpenTracking,
        defaultTemplateEngine,
        // Clone the object since JS objects are passed by reference
        identifierSettings: { ...identifierSettings },
      };
    }
  }

  @action
  toggleMigrationModal() {
    this.isMultiIdMigrationModalOpen = !this.isMultiIdMigrationModalOpen;
    this.activeModal = this.migrateTo;
  }

  @action
  onCloseMigrationModal() {
    this.isMultiIdMigrationModalOpen = false;
    this.activeModal = null;
  }

  @action
  toggleEmailOpenTracking(value: boolean) {
    this.validations.set('isTouched', true);
    set(this.mutableEnvironment, 'emailOpenTracking', value);
  }

  @action
  toggleEmailOpenTrackingModal() {
    this.isEmailTrackingModalOpen = !this.isEmailTrackingModalOpen;
  }

  @action
  toggleChangeIdentifiersById(value: boolean) {
    this.validations.set('isTouched', true);
    set(this.mutableEnvironment.identifierSettings, 'identify_by_id', value);
  }

  @action
  changeIdMutability(value: boolean) {
    this.validations.set('isTouched', true);
    let identifierState = value ? IdentifierState.Enabled : IdentifierState.EnabledImmutable;
    let id = { id: identifierState } as Record<IdentifierKey, IdentifierState>;
    if (this.mutableEnvironment.identifiers?.id) {
      set(this.mutableEnvironment, 'identifiers', { ...this.mutableEnvironment.identifiers, ...id });
    }
  }

  @action
  async onMigrationStarted() {
    await this.args.environment.reload();
  }

  @action
  cancel() {
    if (this.args.onCancel) {
      this.args.onCancel();
      return;
    }
    this.setupEnvironment();
  }

  @action
  onFormValueChange(field: keyof EditableEnvironment, value: string) {
    this.mutableEnvironment = {
      ...this.mutableEnvironment,
      [field]: value,
    };
    this.validations.validateAttribute(field);
  }
}
